import { Grid, GridItem, Spinner, VStack } from "@chakra-ui/react"
import { AnimatePresence } from "framer-motion"
import {
  HomeRegistrationFragmentDoc,
  HomeRegistrationsQueryVariables
} from "@pathwright/graphql-client/src/generated"
import { PositioningContextProvider } from "../positioning/PositioningContext"
import { useHomeContext } from "./HomeContext"
import GroupCard from "./components/group/GroupCard"
import PathCardContainer from "./components/path/PathCardContainer"
import PathCard from "./components/path/RegistrationPathCard"
import CopyLinkAddPromptCard from "./components/prompt/CopyLinkAddPromptCard"
import CopyPathPromptCard from "./components/prompt/CopyPathPromptCard"
import LayoutItem, { PositionedLayoutItem } from "./components/ui/LayoutItem"
import SortRegistrations from "./registration/SortRegistrations"
import { filterNestedRegistration, filterRootRegistration } from "./utils"

function Loading() {
  return (
    <VStack
      className="UserPathsList__loading"
      align="center"
      w="full"
      p={2}
      minH="50vh"
      justifyContent="center"
    >
      <Spinner size="lg" color="whiteAlpha.900" />
    </VStack>
  )
}

const HomeMain = ({
  variables
}: {
  variables: HomeRegistrationsQueryVariables
}) => {
  const {
    loading,
    awaitingPositionedRegistrations,
    tips,
    copyLinks,
    registrations,
    groupMemberships
  } = useHomeContext()

  return (
    <PositioningContextProvider
      items={registrations}
      itemKey="registration"
      itemFragmentDocument={HomeRegistrationFragmentDoc}
      context={variables.positioningContext!}
    >
      <>
        <SortRegistrations registrations={registrations} />
        {loading || awaitingPositionedRegistrations ? (
          <Loading />
        ) : (
          <VStack className="UserPathsList" w="100%" pos="relative">
            <VStack
              as="ul"
              gap={3}
              position="relative"
              w="full"
              align="flex-start"
              justify="flex-start"
              // Certain Tip components will cause a parent container to exceed the expected
              // width due to text wrapping. This is likely caused by a flex layout that attempts
              // to fit the text within the container even if it does wrap.
              // Current fix (only one that has worked so far) is to set a max width on this list.
              maxW="500px"
            >
              {/* Render all animatable/layout items as direct, keyed children. */}
              <AnimatePresence
                // Disable animation on initial mount as the items tend to stretch to their
                // full height, which isn't desirable.
                initial={false}
                // Pull a dismissed card out of the list immediately so its exit
                // animation doesn't keep a second copy of the card that remains.
                mode="popLayout"
              >
                {tips.map((tip, i) => (
                  <LayoutItem key={tip?.key ?? `tip-${i}`}>{tip}</LayoutItem>
                ))}
                {/* All Group related items below */}
                {!!groupMemberships.length && (
                  <LayoutItem key="groups">
                    <Grid
                      templateColumns={`repeat(${groupMemberships.length}, max-content)`}
                      justifyContent="center"
                      gap={4}
                    >
                      {groupMemberships.map((groupMembership) => (
                        <GridItem key={groupMembership.id}>
                          <GroupCard groupMembership={groupMembership} />
                        </GridItem>
                      ))}
                    </Grid>
                  </LayoutItem>
                )}
                {/* All Path related items below */}
                <CopyPathPromptCard />
                {copyLinks.map((copyLink) => (
                  // Preferring to key the link via registration ID as that is the same key used
                  // for the registration items (idea is to prevent a layout animation when copied
                  // link is removed and copier registration is added to the registrations list.)
                  <LayoutItem
                    key={`copy-link-${
                      copyLink.copierRegistrationId || copyLink.id
                    }`}
                  >
                    <CopyLinkAddPromptCard copyLink={copyLink} />
                  </LayoutItem>
                ))}
                {registrations
                  .filter(filterRootRegistration)
                  .map((rootRegistration) => (
                    <PositionedLayoutItem
                      key={`registration-${rootRegistration.id}`}
                      position={rootRegistration.position}
                    >
                      <PathCardContainer
                        registration={rootRegistration}
                        nestedRegistrations={registrations.filter(
                          (registration) =>
                            filterNestedRegistration(
                              rootRegistration,
                              registration
                            )
                        )}
                        PathCardComponent={PathCard}
                      />
                    </PositionedLayoutItem>
                  ))}
              </AnimatePresence>
            </VStack>
          </VStack>
        )}
      </>
    </PositioningContextProvider>
  )
}

export default HomeMain
